<!--
// app/pages/[region]/DraftReserves.vue
-->
<script setup lang="ts">
import type {
  AnyDraftPlayer,
  DraftPlayerFoot,
  DraftPlayerRole,
  DraftPlayerType,
} from "~/src/draft/domain/models/DraftPlayer";
import type { MacroFilters, MacroFilter } from "~/src/draft/domain/models/Filters";
import Banner from "~/app/components/Shared/HeaderBannerTitle.vue";
import DraftPlayers from "~/app/components/pages/region-draft/DraftPlayers.vue";
import SearchField from "~/app/components/Shared/SearchField.vue";
import DraftRoleFilter from "~/app/components/pages/region-draft/DraftRoleFilter.vue";
import DraftPlayerFilters from "~/app/components/pages/region-draft/DraftPlayerFilters.vue";
import draftService from "~/src/draft/domain/services/draftService";
import { initModals } from "flowbite";
import { slugify } from "~/src/Shared/utils";
import { useWebContext } from "~/app/composables/webContext";
import { useRegions } from "~/app/composables/regions";
import { useCompetitionStore } from "~/app/stores/competitionStore";
import { useReserves } from "~/app/composables/reserves";
import { getWebContextReserveTeamsData } from "~/src/draft/datasources/reservesTeamsData";

type Filters = {
  originDataRole: DraftPlayerType;
  foot: DraftPlayerFoot | null;
  searchTerm: string | null;
  role: DraftPlayerRole | null;
  height: MacroFilter;
  macros?: MacroFilters;
};

false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    en: "/[region]/draft/reserves",
    es: "/[region]/draft/reservas",
    de: "/[region]/draft/reservierungen",
    fr: "/[region]/draft/reserves",
    it: "/[region]/draft/riserve",
    pt: "/[region]/draft/reservas",
    ar: "/[region]/draft/reserves",
  },
});

definePageMeta({
  middleware: [
    "not-found-world-cup",
    "not-found-nations",
    "not-found-king-of-kings-nations",
    "not-found-france",
    "not-found-germany",
    "not-found-mena",
  ],
});

const { createMinMaxMacrosFilters, findMacroMinMax, findHeightMinMax } = draftService();

const { isKings, isQueens, contextSiteTitle, webContext } = useWebContext();
const { region } = useCompetitionStore();
const { t } = useI18n();
const { isSpain, isAmericas } = useRegions();
const { getReserves } = useReserves();

const draftPlayerFiltersKey = ref<number>(0);
const draftPlayersCardsKey = ref<number>(0);

const initialFilters: Filters = {
  originDataRole: "player",
  height: {
    min: 100,
    max: 250,
    from: 100,
    to: 250,
  },
  foot: null,
  searchTerm: null,
  role: null,
};

const filters = reactive<Filters>(JSON.parse(JSON.stringify(initialFilters)));
const playersData = ref<AnyDraftPlayer[] | undefined>([]);

const players = ref<AnyDraftPlayer[] | undefined>([]);
const goalkeepers = ref<AnyDraftPlayer[] | undefined>([]);

const draftData = async (): Promise<void> => {
  try {
    const reservesTeamsIds = getWebContextReserveTeamsData(webContext);
    const teamIdValue = reservesTeamsIds[region.slug];
    players.value = undefined; //await getReserves(teamIdValue, "player");
    goalkeepers.value = undefined; //await getReserves(teamIdValue, "goalkeeper");

    playersData.value = filters.originDataRole === "goalkeeper" ? goalkeepers.value : players.value;
  } catch (e) {
    console.error(e);
  }
};

await draftData();

const filteredPlayers = computed((): AnyDraftPlayer[] => {
  if (!playersData.value) return [];

  let _players = playersData.value as AnyDraftPlayer[];

  if (filters.role) {
    _players = _players.filter(p => p.role === filters.role);
  }

  if (filters.searchTerm) {
    _players = _players.filter(p => {
      const { searchTerm } = filters as string;
      return (
        slugify(p.name).includes(slugify(searchTerm)) ||
        (p.draftJersey && p.draftJersey.toString().includes(searchTerm)) ||
        (p.draftJerseyPrefix && p.draftJerseyPrefix.toString().toLowerCase().includes(searchTerm)) ||
        (p.draftOrigin && p.draftOrigin.toString().toLowerCase().includes(searchTerm))
      );
    });
  }

  if (filters.foot) {
    _players = _players.filter(p => p.foot === filters.foot);
  }

  if (filters.macros) {
    for (const keyMacro in filters.macros) {
      _players = _players.filter(p => {
        const isAboveMin = p.macros[keyMacro] >= filters.macros[keyMacro].min;
        const isBelowMax = p.macros[keyMacro] <= filters.macros[keyMacro].max;
        return isAboveMin && isBelowMax;
      });
    }
  }

  if (filters.height) {
    _players = _players.filter(p => {
      const isAboveMin = p.height >= filters.height.min;
      const isBelowMax = p.height <= filters.height.max;
      return (isAboveMin && isBelowMax) || !p.height;
    });
  }

  return _players;
});

const updateRangeValues = () => {
  if (filteredPlayers.value && filteredPlayers.value.length > 0) {
    const macroFilters = createMinMaxMacrosFilters(filteredPlayers.value);

    if (macroFilters) {
      filters.macros = macroFilters;

      if (playersData.value) {
        for (const macrosKey in filters.macros) {
          const [from, to] = findMacroMinMax(macrosKey, playersData.value);
          filters.macros[macrosKey].from = from;
          filters.macros[macrosKey].to = to;
        }
      }
    }

    const [minHeight, maxHeight] = findHeightMinMax(playersData.value);

    filters.height = {
      min: minHeight,
      max: maxHeight,
      from: minHeight,
      to: maxHeight,
    };
  } else {
    filters.height = {
      min: 0,
      max: 0,
      from: 0,
      to: 0,
    };
  }
};

const resetFilters = () => {
  filters.macros = undefined;
  filters.foot = initialFilters.foot;
  filters.height = initialFilters.height;
  filters.role = initialFilters.role;
  filters.searchTerm = initialFilters.searchTerm;
  updateRangeValues();
  draftPlayerFiltersKey.value++;
  draftPlayersCardsKey.value++;
};

watch(
  () => filters.originDataRole,
  newValue => {
    playersData.value = newValue === "goalkeeper" ? goalkeepers.value : players.value;
    resetFilters();
  },
  { immediate: true }
);

onUpdated(() => {
  initModals();
});

useSeoMeta({
  title: t("menuItem.reserves"),
  ogTitle: t("menuItem.reserves"),
  description: t("meta.description.reserve.page", { league: region.leagueTitle || contextSiteTitle }),
  ogDescription: t("meta.description.reserve.page", { league: region.leagueTitle || contextSiteTitle }),
});
</script>

<template>
  <banner :title="$t('menuItem.reserves')"></banner>
  <div class="kql-w-container">
    <div class="px-4 lg:pt-16 lg:px-6">
      <div class="flex flex-col sm:flex-row items-center justify-start gap-4 sm:mb-4 lg:mb-7 sm:pt-4">
        <draft-role-filter v-model="filters.originDataRole" @change="updateRangeValues"></draft-role-filter>
        <div class="order-last mb-4 sm:mb-0 sm:order-auto sm:ml-auto w-full sm:max-w-[364px]">
          <search-field v-model="filters.searchTerm"></search-field>
        </div>
      </div>

      <draft-player-filters
        :key="draftPlayerFiltersKey"
        v-if="filters.macros || (!filters.macros && filters.originDataRole === 'reserve')"
        :player-role="filters.originDataRole"
        v-model:playerFoot="filters.foot"
        v-model:playerPosition="filters.role"
        v-model:macros="filters.macros"
        v-model:height="filters.height"
      ></draft-player-filters>

      <div v-if="!!playersData">
        <button
          class="text-primary border-primary border rounded-lg px-3 py-2 text-xs lg:!my-6 mt-4"
          @click="resetFilters"
        >
          {{ $t("draft.player.filters.clean") }}
        </button>
      </div>
    </div>

    <div class="my-6">
      <draft-players :players="filteredPlayers" is-reserves></draft-players>
    </div>
  </div>
</template>

<style scoped></style>
